import { useCurrency, GbpNote } from "../lib/currency";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { SubscriptionTierCard } from "../components/SubscriptionTierCard";
import PromoRedeemBox from "../components/PromoRedeemBox";
import CreatorReferralCard from "../components/CreatorReferralCard";
import {
  Wallet as WalletIcon,
  ArrowDownLeft,
  ArrowUpRight,
  ShieldCheck,
  Clock,
  Receipt,
  Landmark,
  Globe2,
  Send,
  Crown,
  RefreshCw,
  Download,
} from "lucide-react";

const STATUS_STYLE = {
  completed: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  paid: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  in_progress: "text-nexus-blue bg-nexus-blue/10 border-nexus-blue/30",
  processing: "text-nexus-blue bg-nexus-blue/10 border-nexus-blue/30",
  requested: "text-nexus-blue bg-nexus-blue/10 border-nexus-blue/30",
  in_hold: "text-yellow-300 bg-yellow-500/10 border-yellow-500/30",
  delivered: "text-yellow-300 bg-yellow-500/10 border-yellow-500/30",
  disputed: "text-nexus-pink bg-nexus-pink/10 border-nexus-pink/30",
  admin_review: "text-nexus-pink bg-nexus-pink/10 border-nexus-pink/30",
  rejected: "text-nexus-pink bg-nexus-pink/10 border-nexus-pink/30",
  refunded: "text-purple-300 bg-purple-500/10 border-purple-500/30",
  partial: "text-yellow-300 bg-yellow-500/10 border-yellow-500/30",
  withdrawn: "text-zinc-400 bg-white/5 border-white/10",
};

const METHOD_ICON = { uk_bank: Landmark, iban: Globe2, paypal: Send };

let _fmt = (n) => `£${(n < 0 ? -n : n).toFixed(2)}`;
function formatUsd(n) {
  return _fmt(n < 0 ? -n : n);
}
function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function downloadReceipt(txn, role) {
  const lines = [
    "═════════════════════════════════════",
    "     NEXUS · TRANSACTION RECEIPT",
    "═════════════════════════════════════",
    "",
    `Receipt ID   : ${txn.receipt_id}`,
    `Date         : ${formatDate(txn.date)}`,
    `Type         : ${txn.type.replace("_", " ").toUpperCase()}`,
    `Status       : ${txn.status.toUpperCase()}`,
    `Description  : ${txn.description}`,
    `Counterparty : ${txn.counterparty || "—"}`,
    `Amount       : ${txn.amount < 0 ? "-" : "+"}${formatUsd(txn.amount)} GBP`,
    `Account Role : ${role.toUpperCase()}`,
    "",
    "─────────────────────────────────────",
    "Payouts are processed within 2 business days by Nexus.",
    "Questions? support@collabnexus.co.uk",
    "═════════════════════════════════════",
  ].join("\n");
  const blob = new Blob([lines], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${txn.receipt_id}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Wallet() {
  const { user, loading } = useAuth();
  const { fmt: fmtMoney } = useCurrency();
  _fmt = fmtMoney;
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/wallet/me");
      setWallet(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    load();
  }, [user, loading]);

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/withdraw");
      toast.success(`Withdrawal of ${formatUsd(data.amount)} requested`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!wallet) return (
    <div className="min-h-screen grain"><Navbar /><div className="max-w-6xl mx-auto p-10 text-zinc-500 animate-pulse">Loading wallet…</div></div>
  );

  const isCreator = wallet.role === "creator";
  const primaryLabel = isCreator ? "Available payout" : "Escrow held";
  const primaryAmount = isCreator ? wallet.available_balance : wallet.pending_balance;
  const secondaryLabel = isCreator ? "Pending clearance" : "Lifetime spent";
  const secondaryAmount = isCreator ? (wallet.in_hold_balance + wallet.pending_balance) : wallet.lifetime_volume;

  const pm = wallet.payout_method;
  const pmDetail = pm ? (
    pm.type === "uk_bank" ? `${pm.sort_code_masked} · ••••${pm.account_last4}` :
    pm.type === "iban" ? pm.iban_masked :
    pm.type === "paypal" ? pm.paypal_email_masked : ""
  ) : "";
  const MethodIcon = pm ? METHOD_ICON[pm.type] : Landmark;

  const minPayout = wallet.payout_config?.min_payout || 25;
  const holdDays = wallet.payout_config?.hold_period_days || 3;
  const canWithdraw = isCreator && !!pm && wallet.available_balance >= minPayout;
  const disabledReason = !isCreator ? "" :
    !pm ? "Add a payout method first" :
    wallet.available_balance < minPayout ? `Minimum £${minPayout} — you have £${wallet.available_balance.toFixed(2)} cleared` :
    "";

  return (
    <div className="min-h-screen grain">
      <SEO title="Wallet · Payouts and Receipts" description="Nexus wallet — track your escrow, payouts, subscriptions and receipts in one place." path="/wallet" />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback={user?.role === "brand" ? "/brand" : "/creator"} label="Back to dashboard" />
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Wallet</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
              Your <span className="nexus-gradient-text">money on Nexus</span>
            </h1>
            <p className="text-zinc-400 mt-1 text-sm max-w-xl">
              Every hire, refund, payout and subscription — one clean ledger with downloadable receipts.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={refreshing}
            className="bg-white/5 border-white/15 text-white hover:bg-white/10 rounded-full h-10"
            data-testid="wallet-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Balance cards */}
        <div className="grid md:grid-cols-3 gap-5" data-testid="wallet-summary">
          <div className="p-8 rounded-3xl bg-gradient-to-br from-nexus-purple/20 via-nexus-pink/10 to-nexus-blue/20 hairline nexus-border-glow relative overflow-hidden">
            <WalletIcon className="h-6 w-6 text-white/70 mb-4" />
            <div className="text-xs uppercase tracking-widest text-zinc-300 mb-2">{primaryLabel}</div>
            <div className="font-display font-black text-5xl tracking-tighter" data-testid="wallet-primary-balance">
              {formatUsd(primaryAmount)}
            </div>
            <div className="text-xs text-zinc-400 mt-2">{wallet.currency}</div>
            {isCreator && (
              canWithdraw ? (
                <Button
                  onClick={withdraw}
                  disabled={busy}
                  className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 mt-6 w-full h-11"
                  data-testid="wallet-withdraw-btn"
                >
                  <ArrowDownLeft className="h-4 w-4 mr-2" /> Withdraw · {formatUsd(wallet.available_balance)}
                </Button>
              ) : (
                <div className="mt-6 space-y-2">
                  <Button
                    disabled
                    className="w-full h-11 rounded-full bg-white/5 border border-white/10 text-zinc-500 cursor-not-allowed"
                    data-testid="wallet-withdraw-disabled"
                  >
                    Withdraw {wallet.available_balance > 0 ? `· ${formatUsd(wallet.available_balance)}` : ""}
                  </Button>
                  {disabledReason && (
                    <div className="text-[11px] text-zinc-500 text-center" data-testid="wallet-withdraw-reason">{disabledReason}</div>
                  )}
                </div>
              )
            )}
          </div>

          <div className="p-8 rounded-3xl bg-[#0C0C10] hairline">
            <Clock className="h-6 w-6 text-yellow-300 mb-4" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{secondaryLabel}</div>
            <div className="font-display font-black text-4xl tracking-tighter" data-testid="wallet-secondary-balance">
              {formatUsd(secondaryAmount)}
            </div>
            <div className="text-xs text-zinc-500 mt-2">
              {isCreator ? "Clears when brands approve" : "All-time paid on Nexus"}
            </div>
          </div>

          <div className="p-8 rounded-3xl bg-[#0C0C10] hairline">
            <ShieldCheck className="h-6 w-6 text-nexus-blue mb-4" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
              {isCreator ? "Platform fees paid" : "Refunds returned"}
            </div>
            <div className="font-display font-black text-4xl tracking-tighter">
              {formatUsd(isCreator ? wallet.total_creator_fees : wallet.refunded_total)}
            </div>
            <div className="text-xs text-zinc-500 mt-2">
              {isCreator ? "Creators keep 100% — no commission" : "From partial-release disputes"}
            </div>
          </div>
        </div>

        {isCreator && user?.country && user.country !== "GB" && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100" data-testid="intl-payout-note">
            <span className="font-semibold">Payouts are currently to UK bank accounts only.</span> International payouts (US, South Africa, EU and more) are coming — your earnings stay safely in your Nexus wallet until then, and we'll email you the moment withdrawals open for your country.
          </div>
        )}
        {isCreator && <CreatorReferralCard />}

        {/* Payout method + subscription strip */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* Payout method card (creators only) or subscription-only card (brands) */}
          {isCreator ? (
            <div className="glass rounded-2xl p-5 flex items-center justify-between gap-4" data-testid="wallet-payout-method">
              <div className="flex items-center gap-4 min-w-0">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${pm ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-white/5 border border-white/10"}`}>
                  <MethodIcon className={`h-5 w-5 ${pm ? "text-emerald-300" : "text-zinc-400"}`} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {pm ? `Payouts to ${pm.type.replace("_", " ").toUpperCase()}` : "No payout method yet"}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5 truncate">
                    {pm ? `${pm.account_holder} · ${pmDetail}` : `Add UK bank, IBAN or PayPal to withdraw · min £${minPayout} · ${holdDays}-day hold`}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/creator/payout-method")}
                className="bg-white/5 border-white/15 text-white hover:bg-white/10 rounded-full shrink-0"
                data-testid="wallet-manage-payout-method"
              >
                {pm ? "Manage" : "Add"}
              </Button>
            </div>
          ) : (
            <div className="glass rounded-2xl p-5 flex items-center justify-between gap-4" data-testid="wallet-brand-billing">
              <div className="flex items-center gap-4 min-w-0">
                <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Landmark className="h-5 w-5 text-zinc-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Billing & receipts</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Every invoice · exportable CSV · escrow-backed</div>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/brand/billing")}
                className="bg-white/5 border-white/15 text-white hover:bg-white/10 rounded-full shrink-0"
                data-testid="wallet-brand-billing-btn"
              >
                View billing
              </Button>
            </div>
          )}

          {/* Subscription tier card */}
          <SubscriptionTierCard onUpgrade={() => navigate("/pricing")} />
        </div>

        {/* Promo redemption — brands only */}
        {!isCreator && (
          <PromoRedeemBox variant="inline" />
        )}

        {/* Transactions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500">Transactions</h2>
            <span className="text-xs text-zinc-500">{wallet.transactions.length} entries</span>
          </div>
          <div className="rounded-2xl bg-[#0C0C10] hairline overflow-hidden" data-testid="wallet-txn-list">
            {wallet.transactions.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm">
                No transactions yet. Your ledger will build up as you {isCreator ? "get hired" : "hire creators"}.
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {wallet.transactions.map((t) => {
                  const isIncoming = t.amount > 0;
                  return (
                    <div key={t.id} className="p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors" data-testid={`txn-${t.receipt_id}`}>
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${isIncoming ? "bg-emerald-500/10 text-emerald-300" : "bg-white/5 text-zinc-300"}`}>
                        {isIncoming ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">{t.title}</span>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[t.status] || "text-zinc-400 border-white/10 bg-white/5"}`}>
                            {t.status.replace("_", " ")}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-400 mt-1 truncate">{t.description}</div>
                        <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-1.5">
                          <span className="flex items-center gap-1"><Receipt className="h-3 w-3" /> {t.receipt_id}</span>
                          <span>·</span>
                          <span>{formatDate(t.date)}</span>
                          {t.counterparty && <><span>·</span><span className="truncate">{t.counterparty}</span></>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`font-display font-bold ${isIncoming ? "text-emerald-300" : "text-white"}`}>
                          {isIncoming ? "+" : "−"}{formatUsd(t.amount)}
                        </div>
                        <button
                          onClick={() => downloadReceipt(t, wallet.role)}
                          className="text-[11px] text-nexus-blue hover:underline inline-flex items-center gap-1 mt-1"
                          data-testid={`receipt-${t.receipt_id}`}
                        >
                          <Download className="h-3 w-3" /> Receipt
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
